/**
 * VM Creation Wizard Store
 *
 * Manages state for the multi-step VM creation wizard
 * Includes localStorage persistence for draft saving
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  BillingCycleMonths,
  BillingCycleOption,
  ResourceSpecs,
} from "@/types/resource";
import { DEFAULT_VAT_RATE } from "@/types/resource";
import { calculateBillingCyclePricing } from "@/lib/billingPricing";
import {
  isWindowsOSImage,
  type OSImage,
  type OSImageFilters,
  type ProductLineDto,
} from "@/types/osImage";
import { resolveDefaultConfig } from "@/lib/productLinePresets";
import { getDefaultRegion } from "@/constants/regions";

export interface VMWizardDomainOption {
  enabled: boolean;
  fqdn: string;
  tld: string | null;
  registrationYears: number;
  whoisPrivacy: boolean;
  autoRenew: boolean;
  isAvailable: boolean | null; // null = not yet checked
  unitPrice: number | null; // smallest currency unit
  currency: string | null;
  autoBindToVm: boolean;
}

interface NetworkConfig {
  vmName: string;
  /** Live availability of vmName: null = unknown/checking, true/false = checked. */
  vmNameAvailable: boolean | null;
  region: string;
  sshKey?: string;
}

/**
 * Which funding path the customer picked on the payment step. Each path issues a different,
 * non-reversible first request.
 *
 * - `bank`    — create a real order, then settle it by bank transfer.
 * - `qr`      — create a real order, then settle it by scanning a QR bill.
 * - `balance` — create a real order and charge the account balance as soon as it exists.
 * - `trial`   — no order at all: a trial VM funded by sign-up bonus credit.
 *
 * `bank` and `qr` used to be one coarse `pay` choice that expanded into a second method picker
 * after the order existed, so the customer answered "how do you want to pay" twice. They are named
 * here instead, and the concrete method travels with the order into the settlement flow.
 */
export type FundingChoice = "bank" | "qr" | "balance" | "trial";

export interface VMWizardState {
  // Current step (0-4: Product Line → OS Selection → Configure Specs → Review & Billing → Payment Method)
  currentStep: number;

  // Step 0: Product Line Selection
  selectedProductLine: ProductLineDto | null;

  // Step 2: Chip type selection (resolved from selectedProductLine.availableChipTypes)
  selectedChipType: string | null;

  // Step 1: OS Selection
  selectedOS: OSImage | null;
  osImageFilters: OSImageFilters;

  // Step 2: Configure Specs
  specs: ResourceSpecs;

  // Step 3: Network Configuration
  networkConfig: NetworkConfig;

  // Pricing
  estimatedPricing: {
    hourly: number;
    monthly: number;
    currency: string; // ISO 4217 code (USD, VND, JPY)
    vatRate: number; // VAT rate as percentage (e.g., 10 means 10%)
    billingCycleOptions?: BillingCycleOption[];
    componentBreakdown?: Record<string, number>;
  } | null;

  // Payment fields (Steps 4-5)
  paymentCycle: BillingCycleMonths;
  orderId: string | null;
  orderNumber: string | null;
  /** Order total in display units (already converted from the backend's minor units). */
  orderTotal: number | null;
  /** Null for an order settled from account balance: the server writes that Payment row itself
   *  and never returns its id, so the draft has none to keep. */
  paymentId: string | null;
  paymentOrderNumber: string | null;
  /**
   * Settlement state of the order, mirroring the backend PaymentStatus enum (PascalCase, exact
   * member names). Null until a payment exists.
   *
   * - `Pending`              — payment created, customer has not transferred/paid yet.
   * - `AwaitingVerification` — proof uploaded; a human still has to confirm the money arrived.
   * - `Completed`            — money confirmed. Provisioning starts from here.
   * - `Failed`               — verification rejected. The order stays payable by another method.
   * - `Cancelled`            — the payment request was abandoned, not refused.
   */
  paymentStatus:
    | "Pending"
    | "AwaitingVerification"
    | "Completed"
    | "Failed"
    | "Cancelled"
    | null;
  /**
   * How the order is being settled. Mirrors the backend method names.
   *
   * - `QRCode`         — VietQR / T-PayGate. Offered only while the server reports an
   *                      available QR provider (see `useQrPaymentAvailability`).
   * - `BankTransfer`   — manual transfer; needs a proof upload and human verification.
   * - `AccountBalance` — charged straight to tenant balance (bonus credit first, then cash).
   *                      Settles instantly, so it never reaches AwaitingVerification.
   */
  paymentMethod: "QRCode" | "BankTransfer" | "AccountBalance" | null;
  paymentDetails: {
    qrCodeUrl?: string;
    tPaymentRedirectUrl?: string;
    bankAccountNumber?: string;
    bankAccountName?: string;
    bankName?: string;
    amount?: number;
    // QR provider context and T-PayGate specific fields
    qrProvider?: string;
    tPayGateQrImageBase64?: string;
    tPayGateQrContent?: string;
    tPayGateExpiredAt?: string;
    tPayGateBillCode?: string;
  } | null;
  isWizardCompleted: boolean; // Flag to track if wizard has been completed
  orderCreationCompleted: boolean; // Persisted guard: prevents duplicate order on page refresh
  orderCreationStarted: boolean;
  paymentCreationStarted: boolean;

  /**
   * Set the instant the non-idempotent create-trial request goes out, and persisted — unlike
   * `fundingConfirmed` — precisely because a reload in that window must be remembered. The
   * request may have succeeded server-side with the response lost, and the wizard would
   * otherwise reopen on the funding cards offering to buy the VM the customer already has.
   * Cleared by `resetWizard`, which the success path runs before navigating away.
   */
  trialCreationStarted: boolean;

  /**
   * Funding path highlighted on the payment step. Highlighting sends nothing on its own — the
   * customer commits it with the wizard footer's confirm action, which is what sets
   * `fundingConfirmed` and releases the non-idempotent create-order request.
   *
   * Both live outside `partialize` on purpose: a reload must land back on the choice screen
   * rather than resume a committed choice and create an order without a click.
   */
  fundingSelection: FundingChoice;
  fundingConfirmed: boolean;

  /**
   * True only while the funding cards are on screen and every input that decides them — QR
   * availability, the balance and the trial eligibility — has answered. Reported by Step 4, the
   * one component that knows; the footer's confirm action stays disabled until then.
   *
   * Without it that action was live behind the loading spinner, with "bank" pre-selected, so one
   * click opened a bank-transfer order the customer never saw a card for — a checkout that was
   * never really chosen, and a leftover order the customer then has to deal with. Not persisted: it
   * describes what is on screen now, and a reload has to earn it again.
   */
  fundingOptionsReady: boolean;

  // Domain bundle option (Step 3)
  domainOption: VMWizardDomainOption;

  // Actions
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;

  setSelectedProductLine: (line: ProductLineDto) => void;
  setSelectedChipType: (chipType: string | null) => void;

  setSelectedOS: (os: OSImage | null) => void;
  updateOSImageFilters: (filters: Partial<OSImageFilters>) => void;
  clearOSImageFilters: () => void;

  setSpecs: (specs: ResourceSpecs) => void;

  setNetworkConfig: (config: Partial<NetworkConfig>) => void;

  setEstimatedPricing: (
    pricing: {
      hourly: number;
      monthly: number;
      currency: string;
      vatRate?: number;
      billingCycleOptions?: BillingCycleOption[];
      componentBreakdown?: Record<string, number>;
    } | null,
  ) => void;

  // Payment actions
  setPaymentCycle: (cycle: BillingCycleMonths) => void;
  setOrderDetails: (
    orderId: string,
    orderNumber: string,
    total: number,
  ) => void;
  setPaymentDetails: (
    paymentId: string,
    orderNumber: string,
    method: "QRCode" | "BankTransfer" | "AccountBalance",
    details: VMWizardState["paymentDetails"],
  ) => void;
  /** Drops the payment kept for a reload, once it can no longer be paid. The order stays. */
  clearPaymentDetails: () => void;
  updatePaymentStatus: (status: VMWizardState["paymentStatus"]) => void;
  getTotalAmount: () => {
    subtotal: number;
    tax: number;
    total: number;
  } | null;

  resetWizard: () => void;
  clearWizardFormData: () => void; // Clear form data but keep payment info and current step
  markWizardCompleted: () => void; // Mark wizard as completed
  markOrderCreationCompleted: () => void; // Guard against duplicate order on page refresh
  markOrderCreationStarted: () => void;
  markPaymentCreationStarted: () => void;
  clearOrderCreationStarted: () => void;
  clearPaymentCreationStarted: () => void;

  // Funding choice actions (payment step)
  setFundingSelection: (choice: FundingChoice) => void;
  confirmFundingSelection: () => void;
  clearFundingConfirmation: () => void;
  setFundingOptionsReady: (ready: boolean) => void;
  markTrialCreationStarted: () => void;
  clearTrialCreationStarted: () => void;

  // Domain bundle actions
  setDomainOptionEnabled: (enabled: boolean) => void;
  setDomainOptionField: <K extends keyof VMWizardDomainOption>(
    key: K,
    value: VMWizardDomainOption[K],
  ) => void;
  resetDomainOption: () => void;

  // Get complete form data for submission
  getFormData: () => {
    name: string;
    templateId: string;
    productId: string;
    region: string;
    currency: string; // ISO 4217 code from pricing
    /** Display-only specs. Memory is in GB. Use transformToBackendSpecs() for API payloads. */
    specifications: {
      cores: number;
      memory: number; // In GB (display value — NOT MB)
      disk: number;
    };
    configuration?: {
      sshKey?: string;
      productline?: string;
      chipType?: string;
    };
  } | null;
}

const initialState = {
  currentStep: 0,
  selectedProductLine: null,
  selectedChipType: null,
  selectedOS: null,
  osImageFilters: {
    category: undefined,
    osType: undefined,
    search: "",
    featured: undefined,
  },
  specs: {
    cores: 2,
    memory: 4,
    disk: 50,
    bandwidth: 1000,
    backupCount: 0,
    snapshotCount: 0,
    additionalIps: 1,
  },
  networkConfig: {
    vmName: "",
    vmNameAvailable: null,
    region: getDefaultRegion(),
  },
  estimatedPricing: null,
  // Payment fields
  paymentCycle: 1 as const,
  orderId: null,
  orderNumber: null,
  orderTotal: null,
  paymentId: null,
  paymentOrderNumber: null,
  paymentStatus: null,
  paymentMethod: null,
  paymentDetails: null,
  isWizardCompleted: false,
  orderCreationCompleted: false,
  orderCreationStarted: false,
  paymentCreationStarted: false,
  trialCreationStarted: false,
  // QR is the suggested default (Step 4 falls back to bank transfer, then the balance, when it is
  // not offered), but nothing is sent until it is confirmed.
  fundingSelection: "qr" as FundingChoice,
  fundingConfirmed: false,
  fundingOptionsReady: false,
  domainOption: {
    enabled: false,
    fqdn: "",
    tld: null,
    registrationYears: 1,
    whoisPrivacy: false,
    autoRenew: true,
    isAvailable: null,
    unitPrice: null,
    currency: null,
    autoBindToVm: true,
  } satisfies VMWizardDomainOption,
};

export const useVMWizardStore = create<VMWizardState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Leaving the payment step withdraws the funding commitment. Without this, coming back to
      // step 4 after a failed attempt (which clears `orderCreationStarted`) would find the choice
      // still confirmed and fire order creation again with no click.
      setCurrentStep: (step) =>
        set((state) => ({
          currentStep: step,
          fundingConfirmed: step === 4 ? state.fundingConfirmed : false,
        })),

      nextStep: () =>
        set((state) => ({
          currentStep: Math.min(state.currentStep + 1, 4),
        })),

      previousStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 0),
          fundingConfirmed: false,
        })),

      setSelectedProductLine: (line: ProductLineDto) => {
        // Apply the tier's default preset so the wizard opens on a configuration that belongs to the
        // selected tier. When a product line has no presets configured, fall back to the store's
        // neutral baseline specs (2 / 4 / 50) — a starting slider position, not a fabricated preset.
        const preset = resolveDefaultConfig(line);

        set({
          selectedProductLine: line,
          selectedChipType: null,
          estimatedPricing: null,
          specs: {
            cores: preset?.cores ?? 2,
            memory: preset?.memoryGb ?? 4,
            disk: preset?.diskGb ?? 50,
            bandwidth: 1000,
            backupCount: preset?.backupCount ?? line.defaultBackupCount ?? 0,
            snapshotCount:
              preset?.snapshotCount ?? line.defaultSnapshotCount ?? 0,
            additionalIps: preset?.additionalIps ?? 1,
          },
        });
      },

      setSelectedChipType: (chipType) =>
        set({ selectedChipType: chipType, estimatedPricing: null }),

      setSelectedOS: (os) =>
        set((state) => ({
          selectedOS: os,
          // Windows images use password/RDP credentials. Do not retain a key
          // entered for a previously selected Linux image in a persisted draft.
          networkConfig: isWindowsOSImage(os)
            ? { ...state.networkConfig, sshKey: undefined }
            : state.networkConfig,
        })),

      updateOSImageFilters: (filters) =>
        set((state) => ({
          osImageFilters: { ...state.osImageFilters, ...filters },
        })),

      clearOSImageFilters: () =>
        set({
          osImageFilters: {
            category: undefined,
            osType: undefined,
            search: "",
            featured: undefined,
          },
        }),

      setSpecs: (specs) => set({ specs, estimatedPricing: null }),

      setNetworkConfig: (config) =>
        set((state) => ({
          networkConfig: { ...state.networkConfig, ...config },
          // Region is a pricing input. A name/SSH-key edit does not invalidate the quote.
          ...(config.region !== undefined &&
          config.region !== state.networkConfig.region
            ? { estimatedPricing: null }
            : {}),
        })),

      setEstimatedPricing: (pricing) =>
        set({
          estimatedPricing: pricing
            ? {
                ...pricing,
                vatRate: pricing.vatRate ?? DEFAULT_VAT_RATE,
                billingCycleOptions: pricing.billingCycleOptions ?? undefined,
                componentBreakdown: pricing.componentBreakdown ?? undefined,
              }
            : null,
        }),

      // Payment actions
      setPaymentCycle: (cycle) => set({ paymentCycle: cycle }),

      setOrderDetails: (orderId, orderNumber, total) =>
        set({
          orderId,
          orderNumber,
          orderTotal: total,
          orderCreationCompleted: true,
          orderCreationStarted: false,
          // A new order can only arrive after the previous one left the draft, so any payment
          // still recorded here belongs to that older order. Clearing it keeps the persisted
          // payment (which the payment step resumes after a reload) tied to the order on screen.
          paymentId: null,
          paymentOrderNumber: null,
          paymentStatus: null,
          paymentMethod: null,
          paymentDetails: null,
        }),

      markOrderCreationCompleted: () => set({ orderCreationCompleted: true }),
      markOrderCreationStarted: () => set({ orderCreationStarted: true }),
      markPaymentCreationStarted: () => set({ paymentCreationStarted: true }),
      clearOrderCreationStarted: () => set({ orderCreationStarted: false }),
      clearPaymentCreationStarted: () => set({ paymentCreationStarted: false }),

      setFundingSelection: (choice) => set({ fundingSelection: choice }),
      confirmFundingSelection: () => set({ fundingConfirmed: true }),
      clearFundingConfirmation: () => set({ fundingConfirmed: false }),
      setFundingOptionsReady: (ready) => set({ fundingOptionsReady: ready }),
      markTrialCreationStarted: () => set({ trialCreationStarted: true }),
      clearTrialCreationStarted: () => set({ trialCreationStarted: false }),

      setPaymentDetails: (paymentId, orderNumber, method, details) =>
        set({
          paymentId,
          paymentOrderNumber: orderNumber,
          paymentMethod: method,
          paymentDetails: details,
          paymentCreationStarted: false,
        }),

      clearPaymentDetails: () =>
        set({
          paymentId: null,
          paymentOrderNumber: null,
          paymentStatus: null,
          paymentMethod: null,
          paymentDetails: null,
          paymentCreationStarted: false,
        }),

      updatePaymentStatus: (status) => set({ paymentStatus: status }),

      getTotalAmount: () => {
        const state = get();
        // Allow 0 pricing (free tier, promotional, etc.)
        if (
          state.estimatedPricing?.monthly === undefined ||
          state.estimatedPricing?.monthly === null
        )
          return null;

        const pricing = calculateBillingCyclePricing({
          monthlyPrice: state.estimatedPricing.monthly,
          months: state.paymentCycle,
          vatRate: state.estimatedPricing.vatRate ?? DEFAULT_VAT_RATE,
          currency: state.estimatedPricing.currency ?? "VND",
          backendOption: state.estimatedPricing.billingCycleOptions?.find(
            (option) => option.months === state.paymentCycle,
          ),
        });

        return {
          subtotal: pricing.subtotal,
          tax: pricing.tax,
          total: pricing.total,
        };
      },

      resetWizard: () => set(initialState),

      clearWizardFormData: () =>
        set({
          selectedProductLine: null,
          selectedChipType: null,
          selectedOS: null,
          osImageFilters: {
            category: undefined,
            osType: undefined,
            search: "",
            featured: undefined,
          },
          specs: {
            cores: 2,
            memory: 4,
            disk: 50,
            bandwidth: 1000,
            backupCount: 0,
            snapshotCount: 0,
            additionalIps: 1,
          },
          networkConfig: {
            vmName: "",
            vmNameAvailable: null,
            region: getDefaultRegion(),
          },
          estimatedPricing: null,
          paymentCycle: 1 as const,
          domainOption: initialState.domainOption,
          // Keep currentStep, orderId, paymentStatus, etc. for summary display
        }),

      markWizardCompleted: () => set({ isWizardCompleted: true }),

      setDomainOptionEnabled: (enabled) =>
        set((state) => ({
          domainOption: { ...state.domainOption, enabled },
        })),

      setDomainOptionField: (key, value) =>
        set((state) => ({
          domainOption: { ...state.domainOption, [key]: value },
        })),

      resetDomainOption: () => set({ domainOption: initialState.domainOption }),

      getFormData: () => {
        const state = get();

        if (
          !state.selectedOS ||
          !state.networkConfig.vmName ||
          !state.networkConfig.region
        ) {
          return null;
        }

        const productLineSlug = state.selectedProductLine?.slug;
        const chipType = state.selectedChipType;
        const sshKey = isWindowsOSImage(state.selectedOS)
          ? undefined
          : state.networkConfig.sshKey?.trim() || undefined;

        return {
          name: state.networkConfig.vmName,
          templateId: state.selectedOS.id,
          productId: "30000000-0000-0000-0000-000000000001",
          region: state.networkConfig.region,
          currency:
            state.estimatedPricing?.currency ||
            state.selectedProductLine?.currency ||
            "VND",
          // NOTE: this `specifications` block is for review/display only.
          // Step4_PaymentMethod builds the actual API payload via transformToBackendSpecs()
          // which handles the GB→MB conversion independently. Do not spread this into order requests.
          specifications: {
            cores: state.specs.cores ?? 2,
            memory: state.specs.memory ?? 4, // GB — display value, NOT converted to MB
            disk: state.specs.disk ?? 50,
            backupCount: state.specs.backupCount ?? 0,
            snapshotCount: state.specs.snapshotCount ?? 0,
            additionalIps: state.specs.additionalIps ?? 1,
          },
          configuration:
            sshKey || productLineSlug || chipType
              ? {
                  ...(sshKey && { sshKey }),
                  ...(productLineSlug && { productline: productLineSlug }),
                  ...(chipType && { chipType }),
                }
              : undefined,
        };
      },
    }),
    {
      name: "vm-wizard-draft",
      version: 8,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown> & {
          networkConfig?: { region?: string };
          selectedProductLine?: unknown;
        };
        if (version === 0) {
          if (state.networkConfig?.region === "sg-1") {
            state.networkConfig.region = getDefaultRegion();
          }
        }
        if (version <= 1) {
          // selectedProductLine changed from tier string to ProductLineDto object.
          // Cannot reconstruct full DTO from the persisted string — reset to null
          // so the user re-selects their product line on the next wizard visit.
          if (typeof state.selectedProductLine === "string") {
            state.selectedProductLine = null;
          }
        }
        if (version <= 2) {
          // Ensure specs has backupCount, snapshotCount, additionalIps
          // (added for pricing calculation of backups, snapshots, and public IPs)
          const specs = state.specs as Record<string, unknown> | undefined;
          if (specs && typeof specs === "object") {
            if (specs.backupCount === undefined) specs.backupCount = 0;
            if (specs.snapshotCount === undefined) specs.snapshotCount = 0;
            if (specs.additionalIps === undefined) specs.additionalIps = 1;
          }
        }
        if (version <= 3) {
          // selectedChipType added — default to null so chip selection is required on next visit
          if (state.selectedChipType === undefined) {
            state.selectedChipType = null;
          }
        }
        if (version <= 4) {
          // Clamp persisted specs to valid slider option values.
          // Stale state with non-standard values (e.g. cores=3) caused Slider indexOf() to return -1,
          // breaking the spec config buttons and slider rendering.
          const CPU_OPTIONS = [1, 2, 4, 8, 16, 32];
          const MEMORY_OPTIONS = [1, 2, 4, 8, 16, 32, 64];
          const DISK_OPTIONS = [20, 50, 100, 200, 500, 1000, 2000];
          const specs = state.specs as Record<string, unknown> | undefined;
          if (specs && typeof specs === "object") {
            if (!CPU_OPTIONS.includes(specs.cores as number)) specs.cores = 2;
            if (!MEMORY_OPTIONS.includes(specs.memory as number))
              specs.memory = 4;
            if (!DISK_OPTIONS.includes(specs.disk as number)) specs.disk = 50;
          }
        }
        if (version <= 5) {
          // domainOption slice added — seed default for persisted states that lack it.
          if (state.domainOption === undefined) {
            state.domainOption = {
              enabled: false,
              fqdn: "",
              tld: null,
              registrationYears: 1,
              whoisPrivacy: false,
              autoRenew: true,
              isAvailable: null,
              unitPrice: null,
              currency: null,
              autoBindToVm: true,
            };
          }
        }
        if (version <= 6) {
          // Payment fields are no longer persisted (create wizard is a fresh-start flow).
          // Unconditionally clear any stale payment state that may exist from v6 so
          // users don't land on a stale payment screen after upgrading.
          state.orderId = null;
          state.orderNumber = null;
          state.orderTotal = null;
          state.paymentId = null;
          state.paymentOrderNumber = null;
          state.paymentStatus = null;
          state.paymentMethod = null;
          state.paymentDetails = null;
        }
        if (version <= 7) {
          state.orderCreationStarted = false;
          state.paymentCreationStarted = false;
        }
        return state;
      },
      partialize: (state) => ({
        currentStep: state.currentStep,
        selectedProductLine: state.selectedProductLine,
        selectedChipType: state.selectedChipType,
        selectedOS: state.selectedOS,
        specs: state.specs,
        networkConfig: state.networkConfig,
        estimatedPricing: state.estimatedPricing,
        paymentCycle: state.paymentCycle,
        isWizardCompleted: state.isWizardCompleted,
        orderCreationCompleted: state.orderCreationCompleted,
        orderCreationStarted: state.orderCreationStarted,
        paymentCreationStarted: state.paymentCreationStarted,
        trialCreationStarted: state.trialCreationStarted,
        orderId: state.orderId,
        orderNumber: state.orderNumber,
        orderTotal: state.orderTotal,
        paymentId: state.paymentId,
        paymentOrderNumber: state.paymentOrderNumber,
        paymentStatus: state.paymentStatus,
        paymentMethod: state.paymentMethod,
        paymentDetails: state.paymentDetails,
        domainOption: state.domainOption,
        // Persist the active order/payment so refresh resumes the same Bank Transfer flow.
      }),
    },
  ),
);
